/**
 * PlatformLogin — halaman login khusus Platform Owner.
 * TIDAK memakai halaman /login (admin/customer).
 * Presentasi: Dynicty Owner Portal (reuse token/komponen Auth V1); logika auth tidak berubah.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { usePlatformBranding } from "@/context/PlatformBrandingContext";
import { formatApiError, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { PlatformBrandPanel } from "@/components/auth/PlatformBrandPanel";
import { MobileDyniHeader } from "@/components/auth/MobileDyniHeader";
import "@/components/auth/dynictyAuth.css";

const DYNICTY_LOGO = "/brand/dynicty-logo-official.webp";

export default function PlatformLogin() {
  const { user, loading, login } = usePlatformAuth();
  const { platform } = usePlatformBranding();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) nav("/platform", { replace: true });
  }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Berhasil masuk sebagai Platform Owner");
      nav("/platform", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err) || "Login gagal");
    } finally { setBusy(false); }
  };

  const name = platform.platform_name || "Dynicty";
  const tagline = platform.platform_tagline || "BUILD • GROW • EMPOWER";
  const logoSrc = platform.platform_logo ? imageUrl(platform.platform_logo) : DYNICTY_LOGO;

  return (
    <div className="dyn-auth" data-testid="platform-login-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
        <div className="absolute inset-0 dyn-auth-grid opacity-60" />
        <div className="dyn-auth-blob dyn-pulse-glow -top-32 left-1/4 w-96 h-96" style={{ background: "rgba(6,182,212,0.15)" }} />
        <div className="dyn-auth-blob dyn-pulse-glow bottom-10 right-1/4 w-[450px] h-[450px]" style={{ background: "rgba(147,51,234,0.15)" }} />
        <div className="dyn-auth-blob -bottom-20 left-10 w-80 h-80" style={{ background: "rgba(37,99,235,0.15)" }} />
      </div>

      <main className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <PlatformBrandPanel tagline={tagline} />

          <section className="lg:col-span-5 flex flex-col items-center w-full min-w-0">
            <MobileDyniHeader variant="welcome" greeting="Welcome back, Owner!" subline="Your Commerce Control Center" />
            <div className="dyn-glass-card dyn-auth-card w-full max-w-md p-7 sm:p-9 rounded-3xl relative dyn-rise" data-testid="platform-login-card">
              <div className="flex flex-col items-center text-center">
                <img
                  src={logoSrc}
                  alt={name}
                  className="h-12 w-auto max-w-[240px] object-contain"
                  onError={(e) => { e.target.onerror = null; e.target.src = DYNICTY_LOGO; }}
                  data-testid="platform-login-logo"
                />
                <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.24em] px-2.5 py-1 rounded-full dyn-auth-soft" style={{ color: "#67e8f9" }} data-testid="platform-login-badge">
                  <ShieldCheck size={12} /> Owner Access
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-white mt-3" data-testid="platform-login-title">{name} Owner Portal</h1>
                <p className="text-xs sm:text-sm mt-1" style={{ color: "#cbd5e1" }}>Kelola seluruh tenant, template, deployment dan infrastruktur dari satu tempat.</p>
              </div>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs font-medium">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 dyn-auth-icon" size={16} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="owner@dynicty.com"
                      required
                      className="dyn-auth-input pl-9"
                      data-testid="platform-login-email"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium">Password</Label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    testId="platform-login-password"
                  />
                </div>
                <Button type="submit" disabled={busy} className="w-full dyn-auth-btn" data-testid="platform-login-submit">
                  {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <KeyRound size={16} className="mr-2" />}
                  {busy ? "Memproses..." : `Masuk ke ${name}`}
                </Button>
                <p className="text-[11px] text-center pt-1 dyn-auth-muted">
                  Ini bukan halaman admin toko. Jika Anda admin, silakan buka <a href="/login" className="dyn-auth-link" data-testid="platform-login-admin-link">login admin</a>.
                </p>
              </form>

              <div className="mt-8 pt-5 flex flex-col items-center gap-1.5 text-center" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <div className="flex items-center gap-2 text-xs dyn-auth-muted">
                  <span>Powered by</span>
                  <img src={DYNICTY_LOGO} alt="Dynicty" className="h-9 w-auto object-contain" />
                </div>
                <p className="text-[11px] dyn-auth-hint" data-testid="platform-login-copyright">
                  {platform.copyright_text || "© Dynicty"} · v{platform.platform_version || "1.0.0"}
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
